declare global {
  interface Window {
    Swiper: any;
  }
}

export function initBfHeroSlider(root: ParentNode = document): void {
  const sliders = root.querySelectorAll<HTMLElement>('.bf-hero-slider');
  if (!sliders.length) return;

  if (typeof window.Swiper !== 'function') {
    setTimeout(() => initBfHeroSlider(root), 50);
    return;
  }

  // Animate the wave text once on load (not per slide)
  const animateWaveText = (container: HTMLElement) => {
    if (!container) return;

    // Skip if already animated
    if (container.dataset.bfHeroWaveAnimated === '1') return;
    container.dataset.bfHeroWaveAnimated = '1';

    const morphText = container.querySelector('.bf-hero-text-morph') as SVGTextElement | null;
    const textPath = morphText?.querySelector('textPath') as SVGTextPathElement | null;
    const textElement = morphText?.querySelector('.bf-hero-text') as SVGTSpanElement | null;

    if (!morphText || !textPath || !textElement) return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    const easeInOutCubic = (t: number) => t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;

    // Target value to center the text (same as percent animation)
    const to = 50;

    // If reduced motion, set to final state immediately
    if (prefersReducedMotion) {
      textPath.setAttribute('startOffset', `${to}%`);
      morphText.style.opacity = '1';
      textElement.textContent = 'BEIGE WEEK';
      textElement.classList.remove('text-black');
      textElement.classList.add('text-white');
      return;
    }

    // Enable hardware acceleration
    textElement.style.willChange = 'transform, opacity';
    morphText.style.willChange = 'opacity';

    // Reset to initial state - batch all DOM writes together
    textPath.setAttribute('startOffset', '-25%');
    morphText.style.opacity = '0';
    morphText.style.transition = 'none';
    textElement.textContent = 'BLACK WEEK';
    textElement.classList.remove('text-white');
    textElement.classList.add('text-black');
    textElement.style.opacity = '1';
    textElement.style.transform = 'translate3d(0, 0, 0) scale(1)';

    // Step 1: Animate BLACK WEEK sliding in
    const animateBlackIn = () => {
      let startTs: number | null = null;
      const durationMs = 1200;
      const from = -25;
      
      const frame = (ts: number) => {
        if (startTs === null) {
          startTs = ts;
          morphText.style.opacity = '1';
        }
        const elapsed = ts - startTs;
        const progress = Math.min(1, elapsed / durationMs);
        const eased = easeOutCubic(progress);
        const value = from + (to - from) * eased;
        textPath.setAttribute('startOffset', `${value}%`);
        if (progress < 1) {
          requestAnimationFrame(frame);
        } else {
          // Step 2: Morph word after delay
          setTimeout(() => {
            animateWordMorph();
          }, 600);
        }
      };

      requestAnimationFrame(frame);
    };

    // Step 2: Morph word from BLACK to BEIGE (optimized for performance)
    const animateWordMorph = () => {
      let startTs: number | null = null;
      const durationMs = 800;
      let textChanged = false;
      let colorChanged = false;

      const frame = (ts: number) => {
        if (startTs === null) startTs = ts;
        const elapsed = ts - startTs;
        const progress = Math.min(1, elapsed / durationMs);
        const eased = easeInOutCubic(progress);
        
        // Batch all style updates together
        if (progress < 0.5) {
          // Scale down and fade out BLACK WEEK
          const scale = 1 - eased * 0.5;
          const opacity = 1 - eased;
          // Use translate3d for hardware acceleration
          textElement.style.transform = `translate3d(0, 0, 0) scale(${scale})`;
          textElement.style.opacity = `${opacity}`;
          
          // Change color class once at midpoint (avoid repeated checks)
          if (!colorChanged && progress >= 0.4) {
            colorChanged = true;
            textElement.classList.remove('text-black');
            textElement.classList.add('text-white');
          }
        } else {
          // Change text content once at midpoint (before scaling up)
          if (!textChanged) {
            textChanged = true;
            textElement.textContent = 'BEIGE WEEK';
          }
          
          // Scale up and fade in BEIGE WEEK
          const newProgress = (progress - 0.5) * 2;
          const newEased = easeInOutCubic(newProgress);
          const scale = 0.5 + newEased * 0.5;
          const opacity = newEased;
          textElement.style.transform = `translate3d(0, 0, 0) scale(${scale})`;
          textElement.style.opacity = `${opacity}`;
        }
        
        if (progress < 1) {
          requestAnimationFrame(frame);
        } else {
          // Clean up will-change after animation completes
          textElement.style.willChange = 'auto';
          morphText.style.willChange = 'auto';
          
          // Ensure final state
          textElement.textContent = 'BEIGE WEEK';
          textElement.classList.remove('text-black');
          textElement.classList.add('text-white');
          textElement.style.opacity = '1';
          textElement.style.transform = 'translate3d(0, 0, 0) scale(1)';
        }
      };

      requestAnimationFrame(frame);
    };

    // Start animation sequence
    requestAnimationFrame(() => {
      animateBlackIn();
    });
  };


  sliders.forEach((slider) => {
    if (slider.dataset.init === 'true') return;
    slider.dataset.init = 'true';

    new window.Swiper(slider, {
      slidesPerView: 1,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      loop: true,
      autoplay: {
        delay: 2000,
        disableOnInteraction: false,
      },
      speed: 1000,
      allowTouchMove: false,
      on: {
        init: function (swiper: any) {
          // Delay animation to avoid initialization lag and let layout settle
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const section = slider.closest('.bf-hero-slider-section') as HTMLElement;
              if (section) {
                const containers = section.querySelectorAll('[data-bf-hero-wave]') as NodeListOf<HTMLElement>;
                containers.forEach((container) => {
                  animateWaveText(container);
                });
              }
            });
          });
        }
      }
    });
  });
}
